import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { postApi } from "../../services/api/postApi";
import CommentInput from "./CommentInput";
import UserAvatar from "./UserAvatar";
import VerifiedBadge from "./VerifiedBadge";
import ReportModal from "./ReportModal";
import ConfirmModal from "./ConfirmModal";
import { formatMentions } from "../../utils/formatMentions";
import { formatDistanceToNow } from "date-fns";
import { FiHeart, FiCornerDownRight, FiTrash2, FiFlag } from "react-icons/fi";
import "./CommentSection.css";

const Comment = ({
  comment,
  postId,
  onDelete,
  currentUserId,
  onReplyAdded,
}) => {
  const [liked, setLiked] = useState(comment.liked || false);
  const [likeCount, setLikeCount] = useState(comment.likeCount || 0);
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [localReplies, setLocalReplies] = useState(comment.replies || []);

  // Convert both IDs to strings for comparison (handles ObjectId vs String)
  const isAuthor = String(comment.user?._id) === String(currentUserId);

  const handleLike = async () => {
    const previousLiked = liked;
    const previousCount = likeCount;

    // Optimistic update
    setLiked(!liked);
    setLikeCount(liked ? likeCount - 1 : likeCount + 1);

    try {
      await postApi.toggleCommentLike(postId, comment._id);
    } catch (error) {
      // Rollback on error
      setLiked(previousLiked);
      setLikeCount(previousCount);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await postApi.deleteComment(postId, comment._id);
      onDelete(comment._id);
      setShowDeleteModal(false);
    } catch (error) {
      alert("Failed to delete comment");
    } finally {
      setDeleting(false);
    }
  };

  const handleReply = async (content) => {
    try {
      const response = await postApi.addReply(postId, comment._id, content);
      const newReply = response.data.reply;

      // Add reply to local state (no page reload!)
      setLocalReplies([...localReplies, newReply]);
      setShowReplyInput(false);

      // Notify parent if needed
      if (onReplyAdded) {
        onReplyAdded(comment._id, newReply);
      }
    } catch (error) {
      alert("Failed to post reply");
    }
  };

  return (
    <motion.div
      className="cbx-comment"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      <UserAvatar
        user={comment.user}
        size="medium"
        showBorder={false}
        className="cbx-comment__avatar"
      />
      <div className="cbx-comment__content">
        <div className="cbx-comment__header">
          <div className="cbx-comment__author-wrapper">
            <span className="cbx-comment__author">
              {comment.user?.name || "Unknown User"}
            </span>
            <VerifiedBadge user={comment.user} />
          </div>
          <span className="cbx-comment__time">
            {formatDistanceToNow(new Date(comment.createdAt), {
              addSuffix: true,
            })}
          </span>
        </div>
        <p className="cbx-comment__text">{formatMentions(comment.content)}</p>

        <div className="cbx-comment__actions">
          <motion.button
            className={`cbx-comment__action-btn ${
              liked ? "cbx-comment__action-btn--active" : ""
            }`}
            onClick={handleLike}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FiHeart style={{ fill: liked ? "currentColor" : "none" }} />
            {likeCount > 0 && <span>{likeCount}</span>}
          </motion.button>
          <motion.button
            className="cbx-comment__action-btn"
            onClick={() => setShowReplyInput(!showReplyInput)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FiCornerDownRight /> Reply
          </motion.button>
          {isAuthor ? (
            <motion.button
              className="cbx-comment__action-btn cbx-comment__action-btn--delete"
              onClick={handleDeleteClick}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FiTrash2 /> Delete
            </motion.button>
          ) : (
            <motion.button
              className="cbx-comment__action-btn cbx-comment__action-btn--report"
              onClick={() => setShowReportModal(true)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FiFlag /> Report
            </motion.button>
          )}
        </div>

        <AnimatePresence>
          {showReplyInput && (
            <motion.div
              className="cbx-comment__reply-input"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
            >
              <CommentInput
                onSubmit={handleReply}
                placeholder="Write a reply..."
                buttonText="Reply"
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Replies */}
        {localReplies && localReplies.length > 0 && (
          <div className="cbx-reply-list">
            {localReplies.map((reply, index) => (
              <motion.div
                key={reply._id}
                className="cbx-reply"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <UserAvatar
                  user={reply.user}
                  size="small"
                  className="cbx-reply__avatar"
                />
                <div className="cbx-reply__content">
                  <div className="cbx-reply__header">
                    <span className="cbx-reply__author">
                      {reply.user?.name || "Unknown User"}
                    </span>
                    <span className="cbx-reply__time">
                      {formatDistanceToNow(new Date(reply.createdAt), {
                        addSuffix: true,
                      })}
                    </span>
                  </div>
                  <p className="cbx-reply__text">{formatMentions(reply.content)}</p>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Report Modal */}
      {showReportModal && (
        <ReportModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          contentType="comment"
          contentId={{ postId, commentId: comment._id }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Comment?"
        message="Are you sure you want to delete this comment? This action cannot be undone."
        confirmText="Delete Comment"
        cancelText="Cancel"
        type="danger"
        loading={deleting}
      />
    </motion.div>
  );
};

const CommentSection = ({ postId, commentCount, onUpdate }) => {
  const [localComments, setLocalComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const user = JSON.parse(localStorage.getItem("user"));
  // Try multiple possible locations for user ID
  const currentUserId =
    user?._id || user?.user?._id || user?.userId || user?.id;

  // Lazy load comments when component mounts
  useEffect(() => {
    const fetchComments = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await postApi.getComments(postId);
        setLocalComments(response.data.comments || []);
      } catch (err) {
        setError("Failed to load comments");
      } finally {
        setLoading(false);
      }
    };

    fetchComments();
  }, [postId]);

  // Add new comment
  const handleAddComment = async (content) => {
    try {
      const response = await postApi.addComment(postId, content);
      const newComment = response.data.comment;

      setLocalComments([...localComments, newComment]);
      if (onUpdate) {
        onUpdate(postId, {
          comments: [...localComments, newComment],
          commentCount: (commentCount || 0) + 1,
        });
      }
    } catch (error) {
      alert("Failed to add comment");
    }
  };

  // Delete comment
  const handleDeleteComment = (commentId) => {
    const updatedComments = localComments.filter((c) => c._id !== commentId);
    setLocalComments(updatedComments);

    if (onUpdate) {
      onUpdate(postId, {
        comments: updatedComments,
        commentCount: updatedComments.length,
      });
    }
  };

  return (
    <div className="cbx-comment-section">
      {/* Comment Input */}
      <CommentInput onSubmit={handleAddComment} />

      {/* Loading State */}
      {loading && (
        <div className="cbx-comment-loading">
          <p>Loading comments...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="cbx-comment-error">
          <p>{error}</p>
        </div>
      )}

      {/* Comments List */}
      <AnimatePresence>
        {!loading && !error && localComments.length > 0 && (
          <motion.div
            className="cbx-comment-list"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            {localComments.map((comment, index) => (
              <Comment
                key={comment._id}
                comment={comment}
                postId={postId}
                onDelete={handleDeleteComment}
                currentUserId={currentUserId}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Empty State */}
      {!loading && !error && localComments.length === 0 && (
        <p className="cbx-comment-empty">
          No comments yet. Be the first to comment!
        </p>
      )}
    </div>
  );
};

export default CommentSection;
